import * as CordovaSQLiteDriver from 'localforage-cordovasqlitedriver';
import { Injectable } from '@angular/core';
import { Storage } from '@ionic/storage-angular';
import { flattenMap, genVr } from '../../core/utils';
import { getCtx } from '../../core/context';

@Injectable({
  providedIn: "root",
})
export class StorageService {
  public static instance: StorageService;
  private _storage: Storage | null = null;
  private _cache: Map<string, Map<string, any>> = new Map<string, Map<string, any>>();
  private _version: string;
  private _ready: boolean = false;
  public get ready() {
    return this._ready;
  }
  public get version() {
    return this._version;
  }

  constructor(private storage: Storage) {}

  async init() {
    if (this._storage != null) {
      return;
    }
    await this.storage.defineDriver(CordovaSQLiteDriver);
    const storage = await this.storage.create();
    this._storage = storage;
    StorageService.instance = this;
    const version = "0";
    this._version = await this.getItem("version", version);
    if (this._version == version) await this.saveVersion();
    console.log("*** SERVICE - Storage Service initialized");
    this._ready = true;
  }

  public async getItem<T = string | number | any>(_key: string, _default: T = null): Promise<T> {
   
    await this.init();

    const data = await this.storage?.get(_key);
    if (!data) {
      return _default;
    }

    if (!isNaN(data)) {
      return +data as any;
    }  else {
      try {
        return JSON.parse(data);
      } catch(e) {
        return data;
      }
    }

  }

  public async getCacheItem<T>(_key: string, id?: string): Promise<T> {
    if (!this._cache.has(_key)) {
      const storage = await this.getItem<T>(_key);
      if (storage) {
        await this._cache.set(_key, new Map(storage instanceof Array ? storage.map(i => [i["id"], i]) : Object.entries(storage)));
      } else {
        return null;
      }
    }
 
    return id ? this._cache.get(_key).get(id) : this._cache.get(_key);
  }

  public async setCacheItem<T>(_key: string, _id: string, _value: T, storagePersist: boolean = true) {
    if (!this._cache.has(_key)) {
      this._cache.set(_key, new Map<string, any>());
    }
    if (_id) this._cache.get(_key).set(_id, _value);
    if (storagePersist) {
      const data = flattenMap(this._cache.get(_key));
      for (let key in data) {
        data[key] = data[key]?.export?.call() || data[key];
      }

      await this.setItem(_key, data)
    }
  }

  public async setCacheItems<T>(_key: string, _value: T[], _cacheMapAttr: string = "id", storagePersist: boolean = true) {
  
    _value = Object.values(_value);

    if (!this._cache.has(_key)) {
      this._cache.set(_key, new Map<string, any>());
    }
    for (let item of _value) {
      if (item[_cacheMapAttr]) this._cache.get(_key).set(item[_cacheMapAttr], item);
    }
    let data = this._cache.get(_key);

    if (data instanceof Map) {

      const finalData: any = Object.create({});
      for (const [key, value] of data.entries()) {
        finalData[key] = value;
      }
      data = finalData;
    }

    if (storagePersist) {
      return await this.setItem(_key, data);
    }
    return data;
  }

  public async setItem<T>(_key: string, _value: T, _storeCache?: boolean, _cacheMapAttr: string = "id") {
    await this.init();
    const ctx = getCtx();
    try {
      return await this.storage?.set(_key, JSON.stringify(_value));
    } catch(e) {
      console.log(e)
    } finally {
      if (ctx?.version) {
        this.setVersion(ctx.version)
      }
    }

  }

  public setVersion(v = genVr()) {
    this._version = v;
    this.saveVersion();
  }

  private async saveVersion() {
    try {
      await this.storage?.set("version", this._version);

    } catch(e) {
      console.log(e);
    }
  }

  public async deleteCacheItem<T>(_key: string, _id: string, soft: boolean = false, storagePersist: boolean = true) {
    if (this._cache.has(_key) && this._cache.get(_key).has(_id)) {
      if (soft) {
        const deletedData = this._cache.get(_key).get(_id);
        this._cache.get(_key).set(_id, {
          ...deletedData,
          deleted: true
        })
      } else {
        this._cache.get(_key).delete(_id);
      }
      if (storagePersist) {
        const data = flattenMap(this._cache.get(_key));
        for (let key in data) {
          data[key] = data[key]?.export?.call() || data[key];
        }
  
        await this.setItem(_key, data)
      }
    }
  }
}
